import { Typography, Stack, Box, Chip } from '@mui/material';
import { useFan2Fan } from '@/store';
import TicketCard from './TicketCard';
import Incrementer from '@/components/Incrementer';
import Iconify from '@/components/Iconify';
import useMobile from '@/hooks/useMobile';
import { useTicketItemFacade } from './useTicketItemFacade';

// =============================================================================
// COMPONENTE PRINCIPAL: TicketItem
// =============================================================================

/**
 * Componente compound para mostrar un tipo de boleto.
 *
 * @param {Object} props
 * @param {Object}  props.ticket      - Datos del tipo de boleto
 * @param {Function} props.onChange    - Callback al cambiar cantidad
 * @param {boolean} props.withMap      - Si true, usa MapTicketItemUI
 */
export default function TicketItem({
  ticket,
  quantity = 0,
  actionHandler,
  withMap,
}) {
  const { setShowFan2Fan } = useFan2Fan();

  const ticketData = useTicketItemFacade({
    withMap,
    ticket,
    quantity,
    onChange: actionHandler,
    setShowFan2Fan,
  });

  if (withMap) {
    return <MapTicketItemUI {...ticketData} />;
  }

  return <NoMapTicketItemUI {...ticketData} setShowFan2Fan={setShowFan2Fan} />;
}

// =============================================================================
// COMPONENTE UI: Sin Mapa
// =============================================================================

function NoMapTicketItemUI({
  name,
  price,
  hasDiscount,
  discountType,
  priceWithDiscount,
  bundleRule,
  isFree,
  hasAdditionalFee,
  categoryColor,
  hasDescription,
  ticketDescription,
  quantity,
  maxPerType,
  disableByBookingMax,
  isSoldOut,
  handlers,
  getVariant,
  fan2fanUrl,
  setShowFan2Fan,
}) {
  return (
    <TicketCard
      variant={getVariant(['default', 'promotion', 'fan2fan', 'soldOut'])}
      color={categoryColor}
      styles={{
        '&:hover': {
          borderColor:
            getVariant(['fan2fan']) === 'fan2fan'
              ? (theme) => theme.palette.primary.palette[200]
              : undefined,
          backgroundColor:
            getVariant(['fan2fan']) === 'fan2fan'
              ? (theme) => theme.palette.primary.palette[50]
              : undefined,
        },
      }}
      onClick={() => {
        if (getVariant(['fan2fan']) === 'fan2fan') {
          setShowFan2Fan({ open: true, link: fan2fanUrl });
        }
      }}
    >
      <TicketCard.Body
        withDescription={hasDescription}
        description={ticketDescription}
        disabled={isSoldOut}
      >
        <TicketCard.Info
          title={name}
          variant={getVariant(['default', 'soldOut'])}
        >
          <TicketCard.Price
            value={price}
            hasAdditionalFee={hasAdditionalFee}
            variant={getVariant(['default', 'soldOut', 'fan2fan'])}
            isFree={isFree}
            isSoldOut={isSoldOut}
            withPromotion={hasDiscount}
            discountType={discountType}
            priceWithDiscount={priceWithDiscount}
            bundleRule={bundleRule}
          />
        </TicketCard.Info>

        <TicketCard.Action>
          {isSoldOut && <TicketCard.SoldOut />}
          {getVariant(['fan2fan']) === 'fan2fan' && (
            <Stack role="button" justifyContent="center" alignItems="center">
              <Iconify icon="ph:caret-right" />
            </Stack>
          )}
          {!isSoldOut && getVariant(['fan2fan']) !== 'fan2fan' && (
            <Incrementer
              value={quantity}
              max={maxPerType}
              disabledIncrementer={disableByBookingMax || quantity >= maxPerType}
              onChange={(qty) => {
                handlers(qty);
              }}
            />
          )}
        </TicketCard.Action>
      </TicketCard.Body>
    </TicketCard>
  );
}

// =============================================================================
// COMPONENTE UI: Con Mapa
// =============================================================================

function MapTicketItemUI({
  name,
  categoryColor,
  quantity,
  isSoldOut,
  price,
  hasDiscount,
  discountType,
  priceWithDiscount,
  bundleRule,
  isFree,
  hasAdditionalFee,
  categoryName,
  hasDescription,
  ticketDescription,
  maxPerType,
  handlers,
  getVariant,
}) {
  const { isMobile } = useMobile();
  const isSelected = quantity > 0;

  return (
    <TicketCard
      variant={getVariant(['default', 'promotion', 'fan2fan', 'soldOut'])}
      color={categoryColor}
      isSelected={isSelected}
      quantity={quantity}
      styles={{
        'opacity': isSoldOut ? 0.5 : 1,
        'cursor': isSoldOut ? 'normal' : 'pointer',
        'borderColor': isSelected
          ? (theme) => theme.palette.primary.palette[200]
          : undefined,
        'backgroundColor': isSelected
          ? (theme) => theme.palette.primary.palette[50]
          : undefined,
        '&:hover': {
          borderColor: isSoldOut
            ? undefined
            : (theme) => theme.palette.primary.palette[200],
          backgroundColor: isSoldOut
            ? undefined
            : (theme) => theme.palette.primary.palette[50],
        },
      }}
      onClick={handlers}
    >
      <TicketCard.Body
        withDescription={hasDescription}
        description={ticketDescription}
      >
        <TicketCard.Info title={name} withPromotion={hasDiscount} styles={{ width: 'auto' }}>
          <Typography
            sx={{
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: '130%',
              color: (theme) => theme.palette.text.disabled,
            }}
          >
            {categoryName}
          </Typography>
        </TicketCard.Info>

        {/* ── Acción: Flecha de navegación ── */}
        <TicketCard.Action withPromotion={hasDiscount} styles={{ width: 'fit-content' }}>
          {isSoldOut ? (
            <TicketCard.SoldOut />
          ) : (
            <>
              <TicketCard.Price
                value={price}
                hasAdditionalFee={hasAdditionalFee}
                withMap={true}
                variant={getVariant(['fan2fan'])}
                isFree={isFree}
                isSoldOut={isSoldOut}
                withPromotion={hasDiscount}
                discountType={discountType}
                priceWithDiscount={priceWithDiscount}
                bundleRule={bundleRule}
              />
              <Stack role="button" justifyContent="center" alignItems="center">
                <Iconify icon="ph:caret-right-bold" />
              </Stack>
            </>
          )}
        </TicketCard.Action>
      </TicketCard.Body>
    </TicketCard>
  );
}
